import { observer } from 'mobx-react-lite'
import GroupOutlined from '@mui/icons-material/GroupOutlined'
import useInitialMount from 'reusable/hooks/useInitialMount'
import useStore from 'reusable/mobx-store-utils'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import XeroXpmConectStore, { XeroOriginType } from './XeroXpmConnectStore'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import AdvisorIntegrationCard from 'features/org/edit/integrations/AdvisorIntegrationCard'
import ClientSyncAction from 'features/org/edit/integrations/ClientSyncAction'
import { IntegrationCapabilityRowProps } from 'features/org/edit/integrations/IntegrationCapabilityRow'
import { useXpmConnectionStore } from './XpmConnectionStore'

interface IXeroXpmConnnectProps {
   origin?: XeroOriginType
   /**
    * When true and the integration is connected, shows a "Sync now" button in place of
    * the chip + hover-disconnect affordance.
    */
   showSyncAction?: boolean
   /** Where the card shows the org's client source, if anywhere: the create client modal's main row or the integrations page's capability rows. */
   clientSourceSlot?: 'main-row' | 'rows'
}

export default observer(function XeroXpmConnect(props: IXeroXpmConnnectProps) {
   const orgGuardStore = useOrgGuardStore()
   const xpmConnectionStore = useXpmConnectionStore()
   const xeroXpmConectStore = useStore(sp => new XeroXpmConectStore(sp), { originType: props.origin || 'app', orgGuardStore, xpmConnectionStore, clientSourceSlot: props.clientSourceSlot })

   useInitialMount(() => {
      xeroXpmConectStore.initialiseSettings()
   })

   const connectedAction = props.showSyncAction && xeroXpmConectStore.integrationCardStatus === 'connected'
      ? { label: 'Sync now', onClick: xeroXpmConectStore.syncNow, loading: xeroXpmConectStore.isSyncing }
      : undefined
   const clientSource = xeroXpmConectStore.clientSource
   const showClientsRow = xeroXpmConectStore.showClientSourceRow && !xeroXpmConectStore.showFyiHoldsSourceNote

   return <AdvisorIntegrationCard
      title="Xero Practice Manager"
      subTitle={`Sync clients from ${xeroXpmConectStore.xpmName || 'XPM'}`}
      loading={xeroXpmConectStore.busy}
      status={xeroXpmConectStore.integrationCardStatus}
      onClick={xeroXpmConectStore.integrationCardStatus === 'not connected' ? xeroXpmConectStore.connect : xeroXpmConectStore.disconnect}
      icon={<img src="/images/icons/xero-logo.svg" width="35" />}
      connectedAction={connectedAction}
      clientSource={props.clientSourceSlot === 'main-row' ? clientSource : undefined}
      rows={showClientsRow ? [clientsCapabilityRow(xeroXpmConectStore)] : undefined}
      note={xeroXpmConectStore.showFyiHoldsSourceNote ? 'FYI is your client source. After connecting, you can set XPM as the source.' : undefined}
   />
})

/** The Clients capability row, carrying whichever action the store says the card can offer. */
function clientsCapabilityRow(xeroXpmConectStore: XeroXpmConectStore): IntegrationCapabilityRowProps {
   const { description, action } = xeroXpmConectStore.clientsRow
   return {
      id: 'clients',
      icon: <GroupOutlined />,
      label: 'Clients',
      description,
      action: clientsRowAction(xeroXpmConectStore, action),
   }
}

function clientsRowAction(xeroXpmConectStore: XeroXpmConectStore, action: XeroXpmConectStore['clientsRow']['action']): React.ReactNode {
   switch (action) {
      case 'sync':
         return <ClientSyncAction state={xeroXpmConectStore.isSyncing ? 'syncing' : 'idle'} onSyncNow={xeroXpmConectStore.syncNow} />
      case 'set-source':
         return <ButtonWithProgress variant="text" size="small" onClick={xeroXpmConectStore.setAsClientSource} loading={xeroXpmConectStore.connection.settingSource}>
            Set as client source
         </ButtonWithProgress>
      case 'none':
         return undefined
   }
}
